"use client";

import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  loadMyBooksFilter,
  saveMyBooksFilter,
} from "@/lib/actions/my-books-filter";
import { useAuth } from "@/contexts/auth-context";
import { LOCAL_BYPASS_USER_ID } from "@/lib/local-bypass-auth";
import { safeGet, safeSet } from "@/components/onboarding/safe-storage";

const DEVICE_KEY = "upsider_my_books_filter";
const STORAGE_PREFIX = "upsider_my_books_filter:";

let cached: boolean | undefined;
let inFlight: { readonly userId: string } | null = null;
const listeners = new Set<() => void>();

function publish(value: boolean): void {
  cached = value;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function parseFlag(raw: string | null): boolean | undefined {
  if (raw === "1") return true;
  if (raw === "0") return false;
  return undefined;
}

function readStored(userId: string): boolean | undefined {
  const device = parseFlag(safeGet(DEVICE_KEY));
  if (device !== undefined) return device;
  if (!userId) return undefined;
  return parseFlag(safeGet(storageKey(userId)));
}

function writeStored(userId: string, value: boolean): void {
  const flag = value ? "1" : "0";
  safeSet(DEVICE_KEY, flag);
  if (userId) safeSet(storageKey(userId), flag);
}

export function clearMyBooksFilterCache(): void {
  cached = undefined;
  inFlight = null;
}

function loadFor(userId: string): void {
  if (inFlight?.userId === userId) return;
  const request = { userId };
  inFlight = request;
  void loadMyBooksFilter()
    .then((result) => {
      if (inFlight !== request || cached !== undefined || typeof result !== "boolean") return;
      writeStored(userId, result);
      publish(result);
    })
    .catch(() => {})
    .finally(() => {
      if (inFlight === request) inFlight = null;
    });
}

export function useMyBooksFilter(): {
  myBooksOnly: boolean;
  setMyBooksOnly: (next: boolean) => void;
} {
  const { user } = useAuth();
  const userId = user?.id ?? "";
  const myBooksOnly = useSyncExternalStore(
    subscribe,
    () => cached ?? readStored(userId) ?? false,
    () => false,
  );

  useEffect(() => {
    if (inFlight !== null && inFlight.userId !== userId) inFlight = null;
    if (cached !== undefined) return;
    const stored = readStored(userId);
    if (stored !== undefined) {
      cached = stored;
      return;
    }
    if (!userId || userId === LOCAL_BYPASS_USER_ID) return;
    loadFor(userId);
  }, [userId]);

  const setMyBooksOnly = useCallback(
    (next: boolean) => {
      publish(next);
      writeStored(userId, next);
      if (!userId || userId === LOCAL_BYPASS_USER_ID) return;
      void saveMyBooksFilter(next).catch(() => {});
    },
    [userId],
  );

  return { myBooksOnly, setMyBooksOnly };
}
